import { RestCollectionView } from '@grapecity/wijmo.rest';
import * as wijmo from '@grapecity/wijmo';
import { NpSortDsc } from '../../common/types';
import { NpPagingUtils } from '../../common/np-paging-utils';
import { HttpErrorResponse } from '@angular/common/http';
import { Http401Error } from '@newpla/common';

export interface NpScvQueryParams {
    pageOnServer: boolean;
    paging?: any;
    sort?: NpSortDsc[];
}

export interface NpScvPageResult {
    data: any[];
    totalCount: number;
}

export interface NpScvQueryApis<T, R> {
    getItems: (params: T) => Promise<R | any[]>;
    addItem?: (item: any) => Promise<any>;
    patchItem?: (item: any) => Promise<any>;
    deleteItem?: (item: any) => Promise<any>;
}

export abstract class NpScvQueryAdaptor<T, R> {

    private _apis: NpScvQueryApis<T, R>;
    public get apis() {
        return this._apis;
    }

    abstract getItems(queryParams: NpScvQueryParams): Promise<NpScvPageResult | any[]>;
    constructor(apis: NpScvQueryApis<T, R>) {
        this._apis = apis;
    }
}

export class NpServerCollectionView extends RestCollectionView {

    _queryAdaptor: NpScvQueryAdaptor<any, any>;
    _loadCnt = 0;
    _autoLoad: boolean = true;

    constructor(
        queryAdaptor: NpScvQueryAdaptor<any, any>,
        options?: {
            pageOnServer?: boolean,
            sortOnServer?: boolean,
            sortDscs?: NpSortDsc[],
            pageSize?: number,
            trackChanges?: boolean,
            autoLoad?: boolean
        }) {
        let superOptions = options?.trackChanges ? { trackChanges: true } : {}
        super(superOptions);

        // let aaa = new CollectionView([], { trackChanges: true });
        // console.log('ddd');

        // setting default options
        this.pageOnServer = true;
        this.sortOnServer = true;
        this.filterOnServer = false;
        this.pageSize = 0;

        // 서버에서 모두 안한다고 하고 페이징도 없다면 그냥 그리드 바인딩이라 볼 수 있다.
        this._queryAdaptor = queryAdaptor;

        // this.sourceCollection._trackChanges = true;

        if (options?.autoLoad === false) {
            this._autoLoad = false;
        }

        if (options) {
            this.pageOnServer = options.pageOnServer === false ? false : true;
            this.sortOnServer = options.sortOnServer === false ? false : true;
            if (options.pageSize && options.pageSize > 0) {
                this.pageSize = options.pageSize;
            }

            if (options.sortDscs && options.sortDscs.length > 0) {
                this.sortDescriptions.clear();
                options.sortDscs.forEach(s => {
                    this.sortDescriptions.push(new wijmo.SortDescription(s.property, s.asc));
                })
            }
        }

        if (this.pageOnServer && this.pageSize < 1) {
            throw new Error("pageSize cannot be zero if pageOnServer is true");
        }

        this.onError = (e: any) => {
            const response = e._error;
            let errorMessage = '';
            if (response instanceof HttpErrorResponse) {
                if (response.error.message) {
                    errorMessage = response.error.message;
                } else {
                    errorMessage = response.status + ' ' + response.statusText;
                }
            } else {
                errorMessage = response.message;
            }

            if (!(response instanceof Http401Error)) {
                window.alert(errorMessage);
            }
            return false;
        };

    }

    // ** implementation

    // read parameters apply paging
    _getReadParams(): NpScvQueryParams {

        let params: NpScvQueryParams = { pageOnServer: this.pageOnServer };

        // paging on server
        if (this.pageOnServer) {
            let paging: any = {}
            paging.pageIndex = this.pageIndex;
            paging.page = this.pageIndex + 1;
            paging.pageSize = this.pageSize;
            params.paging = paging;
        }

        // sorting on server
        if (this.sortOnServer && this.sortDescriptions.length > 0) {
            let sort: NpSortDsc[] = [];
            for (var i = 0; i < this.sortDescriptions.length; i++) {
                var sd = this.sortDescriptions[i];
                let npFlexGridSort: NpSortDsc = { property: sd.property, asc: sd.ascending };
                sort.push(npFlexGridSort);
            }
            params.sort = sort;

            // sortOnServer 옵션은 서버에서 sorting을 해야만 하는 경우에 필요하다.
            // pageOnServer 옵션을 사용하면서 pageSize > 0 인경우
            if (this.pageOnServer === false) {
                console.debug("you don't need to use sortOnServer. because pageOnServer is false")
            }
        }

        if (this.filterOnServer) {
            throw new Error("NpServerCollectionView does not support [filterOnServer]");
        }

        if (this.pageOnServer && this._filterProvider) {
            throw new Error("Do not use Filter in pageOnServer and pageSize not zero.");
        }

        return params;
    }

    reload() {
        this.load();
    }

    goFirstPage() {
        this._pgIdx = 0;
        this.load();
    }

    public get page() {
        return this._pgIdx + 1;
    }

    public set page(page: number) {

        if (!page || page < 1) {
            this._pgIdx = 0;
        } else {
            this._pgIdx = page - 1;
        }
        this.load();

    }

    setPageSize(value: any) {

        let pageSize = parseInt(value);

        let startIdx = NpPagingUtils.getStartIdx(this._pgIdx, this._pgSz);
        let newPgIdx = NpPagingUtils.getPageIdx(startIdx, pageSize);

        this._pgIdx = newPgIdx;
        this.pageSize = pageSize;

    }

    // ** overrides
    protected override async getItems(): Promise<any[]> {

        if (this._autoLoad === false && this._loadCnt < 1) {
            this._loadCnt++;
            return Promise.resolve([]);
        }

        this._loadCnt++;
        let npsvCvParams: NpScvQueryParams = this._getReadParams();
        let result = await this._queryAdaptor.getItems(npsvCvParams);

        if (this._pageOnServer === true) {

            const pageResult = result as NpScvPageResult
            this._totalItemCount = pageResult.totalCount;
            return pageResult.data;
        } else {
            return result as any[];
        }


    }

    protected override async addItem(item: any): Promise<any> {

        if (!this._queryAdaptor.apis.addItem) {
            throw Error("not implemented!")
        }

        let data = await this._queryAdaptor.apis.addItem(item)
            .catch((reason: any) => {
                this._raiseError(reason, true)
                // this._raiseError(xhr.responseText, true)
            });
        this.refresh();
        return data;

    }

    protected override patchItem(item: any): Promise<any> {

        if (this._queryAdaptor.apis.patchItem) {
            // throw Error(" impnotlemented!")
            return Promise.reject("not implementes")
        }
        return Promise.resolve();

        // if (!this._queryAdaptor.apis.patchItem) {
        //     throw Error("not implemented!")
        // }
        // return this._queryAdaptor.apis.patchItem(item).catch((reason: any) => {
        //     this._raiseError(reason, true)
        //     // this._raiseError(xhr.responseText, true)
        // });
    }

    protected override deleteItem(item: any): Promise<any> {

        if (!this._queryAdaptor.apis.deleteItem) {
            throw Error("not implemented!")
        }

        return this._queryAdaptor.apis.deleteItem(item).catch((reason: any) => {
            this._raiseError(reason, true)
            // this._raiseError(xhr.responseText, true)
        });
    }
}
